import { PWBWorker } from 'promise-worker-bi';

// optional chaining is not supported here
/* eslint-disable @typescript-eslint/prefer-optional-chain */

const promiseWorker = new PWBWorker();

const CHANNELS = ['email', 'twilio', 'push', 'webhook', 'slack', 'urban_airship', 'in_app'];
const CONVERTED_DENOMINATOR = 'converted_denominator';
const TOTAL_CONVERTED_DENOMINATOR = `total_${CONVERTED_DENOMINATOR}`;

function sumArray(arr) {
  if (arr.length === 0) {
    return 0;
  }

  return arr.reduce((a, b) => a + b, 0);
}

function calculateRatios(numerators, denominators) {
  if (numerators == null || denominators == null) {
    return [];
  }

  // Assume that numerators line up with denominators from beginning. If
  // different lengths, slice from the start.
  if (numerators.length < denominators.length) {
    denominators = denominators.slice(0, numerators.length - 1);
  }

  return denominators.map((value, i) => {
    if (value == null || value === 0) {
      return null;
    }

    return numerators[i] / value;
  });
}

export default class TotalAnalysisMetricsWorker {
  constructor() {
    this.findDenominator = this.findDenominator.bind(this);
  }

  findDenominator(measure, measureType, channel) {
    if (measureType === 'delivery_metrics') {
      let measureDefinition = this.deliveryMetricsDenominators[measure];

      if (
        measureDefinition != null &&
        measureDefinition.actionTypeDenominators != null &&
        measureDefinition.actionTypeDenominators[`${channel}_action`] != null
      ) {
        return measureDefinition.actionTypeDenominators[`${channel}_action`];
      }

      return measureDefinition.denominator;
    }

    if (measureType === 'journey_metrics') {
      return this.journeyMetricsDenominators[measure];
    }

    return null;
  }

  process(
    metrics,
    selectedCampaignIds,
    selectedChannels,
    {
      DELIVERY_METRICS_MEASURES,
      JOURNEY_METRICS_MEASURES,
      DELIVERY_METRICS_MEASURE_DENOMINATORS,
      JOURNEY_METRICS_MEASURE_DENOMINATORS,
      METRICS_PER_ACTION_TYPE,
    },
  ) {
    this.deliveryMetricsDenominators = DELIVERY_METRICS_MEASURE_DENOMINATORS;
    this.journeyMetricsDenominators = JOURNEY_METRICS_MEASURE_DENOMINATORS;

    let selectedMetrics = metrics.filter((m) => selectedCampaignIds.includes(String(m.id)));

    let summedMetrics = {
      per_channel_metrics: {},
      delivery_metrics: {},
      journey_metrics: {},
      item_types: [],
    };

    let summedChannelMetrics = {};

    // Prepare arrays for the data
    CHANNELS.forEach((channel) => {
      let deliveryMetrics = {};

      DELIVERY_METRICS_MEASURES.forEach((measure) => {
        deliveryMetrics[measure] = [];
      });

      // Because conversions are special (only metrics from campaigns that have
      // conversion goals should be counted), we need to keep track of the
      // sent/delivered denominators separately
      deliveryMetrics[CONVERTED_DENOMINATOR] = [];

      summedChannelMetrics[channel] = deliveryMetrics;
    });

    function sumChannelMetrics(item, channel, itemMeasureName, currentTotalMeasureName) {
      let currentSummedValues = summedChannelMetrics[channel][currentTotalMeasureName];
      let itemMeasureValues = item.data.per_channel_metrics[channel][itemMeasureName];

      summedChannelMetrics[channel][currentTotalMeasureName] = itemMeasureValues.map((value, index) => {
        let currentValue = currentSummedValues[index];

        if (currentValue != null) {
          return value + currentValue;
        } else {
          return value;
        }
      });
    }

    // Sum up the individual per_channel_metrics arrays from each item into
    // per_channel_metrics total arrays
    selectedMetrics.forEach((m) => {
      CHANNELS.forEach((channel) => {
        DELIVERY_METRICS_MEASURES.forEach((measure) => {
          if (measure === 'converted') {
            // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
            if (m.conversion) {
              let denominator = this.findDenominator(measure, 'delivery_metrics', channel);

              sumChannelMetrics(m, channel, denominator, CONVERTED_DENOMINATOR);
            } else {
              return;
            }
          }

          sumChannelMetrics(m, channel, measure, measure);
        });
      });
    });

    // Calculate total sums and rates for the total per_channel_metrics
    CHANNELS.forEach((channel) => {
      DELIVERY_METRICS_MEASURES.forEach((measure) => {
        let numerators = summedChannelMetrics[channel][measure];

        summedChannelMetrics[channel][`total_${measure}`] = sumArray(numerators);
        let denominator = this.findDenominator(measure, 'delivery_metrics', channel);

        if (measure === 'converted') {
          summedChannelMetrics[channel][TOTAL_CONVERTED_DENOMINATOR] = sumArray(
            summedChannelMetrics[channel][CONVERTED_DENOMINATOR],
          );
          denominator = CONVERTED_DENOMINATOR;
        }

        if (denominator == null) {
          return;
        }

        summedChannelMetrics[channel][`${measure}_rates`] = calculateRatios(
          numerators,
          summedChannelMetrics[channel][denominator],
        );
      });
    });

    summedMetrics.per_channel_metrics = summedChannelMetrics;

    selectedMetrics.forEach((m) => {
      let { data } = m;

      DELIVERY_METRICS_MEASURES.forEach((measure) => {
        let totalKey = `total_${measure}`;
        if (summedMetrics.delivery_metrics[totalKey] == null) {
          summedMetrics.delivery_metrics[totalKey] = data.delivery_metrics[totalKey];
          summedMetrics.delivery_metrics[measure] = data.delivery_metrics[measure];
        } else {
          summedMetrics.delivery_metrics[totalKey] =
            summedMetrics.delivery_metrics[totalKey] + data.delivery_metrics[totalKey];
          summedMetrics.delivery_metrics[measure] = data.delivery_metrics[measure].map((value, index) => {
            let currentValue = summedMetrics.delivery_metrics[measure][index];
            return value + currentValue;
          });
        }
      });

      [...JOURNEY_METRICS_MEASURES, 'in_workflow'].forEach((measure) => {
        let totalKey = `total_${measure}`;

        if (summedMetrics.journey_metrics[totalKey] == null) {
          // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
          summedMetrics.journey_metrics[totalKey] = data.journey_metrics[totalKey] || 0;
          // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
          summedMetrics.journey_metrics[measure] = data.journey_metrics[measure] || [];
        } else {
          if (data.journey_metrics[measure] == null) {
            return;
          }

          summedMetrics.journey_metrics[totalKey] =
            // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
            summedMetrics.journey_metrics[totalKey] + (data.journey_metrics[totalKey] || 0);
          // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
          summedMetrics.journey_metrics[measure] = (data.journey_metrics[measure] || []).map((value, index) => {
            let currentValue = summedMetrics.journey_metrics[measure][index];
            if (currentValue == null) {
              currentValue = 0;
            }
            return value + currentValue;
          });
        }
      });
    });

    DELIVERY_METRICS_MEASURES.forEach((measure) => {
      let numerators = summedMetrics.delivery_metrics[measure];
      let numeratorTotal = summedMetrics.delivery_metrics[`total_${measure}`];

      let denominatorTotal = selectedChannels.reduce((acc, channel) => {
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        if (!METRICS_PER_ACTION_TYPE[`${channel}_action`].includes(measure)) {
          return acc;
        }

        let denominator = this.findDenominator(measure, 'delivery_metrics', channel);

        if (measure === 'converted') {
          denominator = CONVERTED_DENOMINATOR;
        }

        if (denominator == null) {
          return acc;
        }

        let total = summedMetrics.per_channel_metrics[channel][`total_${denominator}`];

        if (total == null) {
          return acc;
        }

        if (acc == null) {
          return total;
        }

        return acc + total;
      }, null);

      summedMetrics.delivery_metrics[`${measure}_rate`] =
        denominatorTotal > 0 ? numeratorTotal / denominatorTotal : null;

      let arrayLength = 0;

      if (numerators != null) {
        arrayLength = numerators.length;
      }

      let denominatorSeries = selectedChannels.reduce((acc, channel) => {
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        if (!METRICS_PER_ACTION_TYPE[`${channel}_action`].includes(measure)) {
          return acc;
        }

        let denominator = this.findDenominator(measure, 'delivery_metrics', channel);

        if (measure === 'converted') {
          denominator = CONVERTED_DENOMINATOR;
        }

        if (denominator == null) {
          return acc;
        }

        return summedMetrics.per_channel_metrics[channel][denominator].map((v, i) => {
          let currentValue = acc[i];

          if (currentValue == null) {
            return v;
          }

          if (v == null) {
            return currentValue;
          }

          return v + currentValue;
        });
      }, new Array(arrayLength).fill(null));

      summedMetrics.delivery_metrics[`${measure}_rates`] = calculateRatios(numerators, denominatorSeries);
    });

    [...JOURNEY_METRICS_MEASURES, 'in_workflow'].forEach((measure) => {
      let denominator = this.findDenominator(measure, 'journey_metrics');
      if (denominator == null) {
        return;
      }
      let numeratorTotal = summedMetrics.journey_metrics[`total_${measure}`];
      let denominatorTotal = summedMetrics.journey_metrics[`total_${denominator}`];

      summedMetrics.journey_metrics[`${measure}_rate`] = denominatorTotal > 0 ? numeratorTotal / denominatorTotal : 0;
      summedMetrics.journey_metrics[`${measure}_rates`] = calculateRatios(
        summedMetrics.journey_metrics[measure],
        summedMetrics.journey_metrics[denominator],
      );
    });

    return summedMetrics;
  }
}

promiseWorker.register(async (processArgs) => {
  let processWorker = new TotalAnalysisMetricsWorker();

  return processWorker.process(...processArgs);
});
